/**
 * Title and Tag-Based Collection Search
 * - Searches for products in collection by title and tags
 * - Simple and focused implementation
 * - Modified to work with custom search-filter template
 */
document.addEventListener("DOMContentLoaded", function () {
    const hiddenProductTitleFragments = [
        "personalized logo fee",
        "personalisation fee(message inside/outside box and logo)",
        "personalisation fee(message inside and outside box)",
        "message inside box only",
        "message outside box only",
        "messages both inside and outside",
    ];

    const hiddenProductHandleFragments = [
        "personalized-logo-fee",
        "personalisation-fee-message-inside-outside-box-and-logo",
        "personalisation-fee-message-inside-and-outside-box",
        "message-inside-box-only",
        "message-outside-box-only",
        "messages-both-inside-and-outside",
    ];

    const hiddenLegacyProductIds = new Set([
        10784157008206,
        15710171234638,
        10495648203086,
        10773350285646,
        10775283827022,
        10775286710606,
        10804915306830,
        10804944896334,
        10813289234766,
        10784156778830,
        10495648006478,
        10495647940942,
        10799963078990,
        10799962882382,
        10799962718542,
        10799962685774,
        10799962390862,
        10799961964878,
    ]);

    function isHiddenPersonalisationProduct(product) {
        const productId = Number(product.id);
        const productTitle = (product.title || '').toLowerCase();
        const productHandle = (product.handle || '').toLowerCase();
        const productTags = Array.isArray(product.tags)
            ? product.tags.map(tag => String(tag).toLowerCase())
            : String(product.tags || '')
                .toLowerCase()
                .split(',')
                .map(tag => tag.trim())
                .filter(Boolean);

        if (hiddenLegacyProductIds.has(productId)) return true;
        if (productTags.includes('hidden-personalisation-fee')) return true;
        if (hiddenProductTitleFragments.some(fragment => productTitle.includes(fragment))) return true;
        if (hiddenProductHandleFragments.some(fragment => productHandle.includes(fragment))) return true;

        return false;
    }

    // Find all search inputs (both banner and CTA)
    const searchInputs = document.querySelectorAll('[id^="banner-search-input-"], [id^="cta-search-input-"]');

    searchInputs.forEach(function(searchInput) {
        // Get the corresponding results element
        let searchResultsId;

        if (searchInput.id.startsWith('banner-search-input-')) {
            searchResultsId = searchInput.id.replace('input', 'results');
        } else if (searchInput.id.startsWith('cta-search-input-')) {
            searchResultsId = searchInput.id.replace('cta-search-input-', 'cta-search-results-');
        }

        const searchResults = document.getElementById(searchResultsId);

        // If no search results container found, skip this search input
        if (!searchResults) return;

        // Get loader ID based on results ID
        const searchLoaderId = searchResultsId.replace('results', 'loader');
        const searchLoader = document.getElementById(searchLoaderId);
        const searchButton = searchInput.nextElementSibling;

        // Get collection information
        const searchContainer = searchInput.closest('.banner-search');
        const collectionHandle = searchContainer?.dataset.collectionHandle;
        const collectionTitle = searchContainer?.dataset.collectionTitle;
        
        console.log(`Search initialized for ${collectionHandle ? `collection: ${collectionTitle} (${collectionHandle})` : 'all products'}`);

        // Store collection products to avoid repeated fetching
        let collectionProductsCache = null;

        // Debounce function to limit API calls
        function debounce(func, wait) {
            let timeout;
            return function(...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), wait);
            };
        }

        // Function to handle search
        const handleSearch = debounce(function() {
            const query = searchInput.value.trim();
            if (query.length < 2) {
                searchResults.innerHTML = "";
                searchResults.style.display = "none";
                return;
            }

            // Show loader
            searchResults.style.display = "block";
            searchLoader.classList.add('active');

            if (collectionHandle) {
                // Search within collection products
                searchCollectionProducts(query);
            } else {
                // Regular search for all products
                searchAllProducts(query);
            }
        }, 300);

        // Function to search within collection products
        function searchCollectionProducts(query) {
            // If we already have the collection products, search within them
            if (collectionProductsCache) {
                filterByTitleAndTags(query, collectionProductsCache);
                return;
            }

            // Otherwise, fetch collection products first
            console.log(`Fetching products for collection: ${collectionHandle}`);
            fetch(`/collections/${collectionHandle}/products.json?limit=250`)
                .then(response => response.json())
                .then(data => {
                    // Cache the collection products
                    collectionProductsCache = data.products;
                    console.log(`Found ${collectionProductsCache.length} products in collection`);
                    
                    // Search within these products
                    filterByTitleAndTags(query, collectionProductsCache);
                })
                .catch(error => {
                    console.error("Error fetching collection products:", error);
                    // Fall back to general search
                    searchAllProducts(query);
                });
        }

        // Function to filter collection products by title and tags
        function filterByTitleAndTags(query, products) {
            console.log(`Filtering ${products.length} collection products for: "${query}" in titles and tags`);

            const visibleProducts = products.filter(product => !isHiddenPersonalisationProduct(product));
            
            // Convert search query to lowercase for case-insensitive matching
            const lowerQuery = query.toLowerCase();
            
            // Filter products that have the query in their title or tags
            const matchedProducts = visibleProducts.filter(product => {
                // Check if the query appears in the title
                const titleMatch = product.title?.toLowerCase().includes(lowerQuery);
                
                // Check if the query appears in any of the tags
                const tagMatch = product.tags && Array.isArray(product.tags) && 
                                product.tags.some(tag => tag.toLowerCase().includes(lowerQuery));
                
                // Return true if it matches either title or tags
                return titleMatch || tagMatch;
            });
            
            console.log(`Found ${matchedProducts.length} matching products by title or tags for "${query}":`);
            matchedProducts.forEach(p => {
                const matchType = [];
                if (p.title?.toLowerCase().includes(lowerQuery)) matchType.push('title');
                if (p.tags && Array.isArray(p.tags) && p.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) {
                    matchType.push('tags');
                }
                console.log(` - ${p.title} (match: ${matchType.join(', ')})`);
            });
            
            // Format the matched products for display
            const formattedProducts = matchedProducts.map(product => ({
                title: product.title,
                url: `/products/${product.handle}`,
                image: product.images && product.images.length > 0 ? product.images[0].src : '',
                price: product.variants && product.variants.length > 0 ? product.variants[0].price : '',
                available: product.variants && product.variants.length > 0 ? product.variants[0].available : true,
            }));
            
            // Display the results
            if (formattedProducts.length > 0) {
                displayResults(formattedProducts, query);
            } else {
                // If no products found in collection, try the general search
                console.log("No matching products found in collection by title or tags, trying general search");
                searchAllProducts(query);
            }
        }

        // Function to search all products (when not in a specific collection or as a fallback)
        function searchAllProducts(query) {
            console.log(`Performing general search for: "${query}"`);
            const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`;

            fetch(searchUrl)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`API Error: ${response.status}`);
                    }
                    return response.json();
                })
                .then(data => {
                    const products = (data.resources.results.products || []).filter(
                        product => !isHiddenPersonalisationProduct(product)
                    );
                    console.log(`General search found ${products.length} products`);
                    
                    if (products.length > 0) {
                        displayResults(products, query);
                    } else {
                        displayNoResults(query);
                    }
                })
                .catch(error => {
                    console.error("Error fetching search results:", error);
                    displayError();
                });
        }

        // Display search results
        function displayResults(products, query) {
            searchLoader.classList.remove('active');
            
            console.log(`Displaying ${products.length} products in search results:`, 
                        products.map(p => p.title));

            let resultsHtml = "";
            
            // Add collection filter notice if applicable
            if (collectionHandle && collectionTitle) {
                resultsHtml += `<div class="search-filter-notice">Showing results for "${query}" in ${collectionTitle}</div>`;
            }

            resultsHtml += "<ul>";
            products.slice(0, 10).forEach((product) => {
                const productImg = product.image || '';
                const price = product.price ? formatPrice(product.price) : '';

                resultsHtml += `
                <li>
                    <a href="${product.url}">
                        <div class="search-result-image">
                            <img src="${productImg}" alt="${product.title}" width="50" height="50" 
                                 onerror="this.src='//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png?v=1530129081'">
                        </div>
                        <div class="search-result-content">
                            <div class="search-result-title">${product.title}</div>
                        </div>
                    </a>
                </li>`;
            });
            resultsHtml += "</ul>";

            // Create view all link - MODIFIED to direct to custom template
            let viewAllUrl;
            if (collectionHandle) {
                // Direct to the same collection with the search filter template and search parameter
                viewAllUrl = `/collections/${collectionHandle}/search-filter?search=${encodeURIComponent(query)}`;
            } else {
                viewAllUrl = `/search?q=${encodeURIComponent(query)}&type=product`;
            }

            resultsHtml += `<a href="${viewAllUrl}" class="banner-view-all">View all results</a>`;

            searchResults.innerHTML = resultsHtml;
            searchResults.appendChild(searchLoader);
            searchResults.style.display = "block";
        }

        // Display no results message
        function displayNoResults(query) {
            searchLoader.classList.remove('active');

            let noResultsHtml = '';
            if (collectionTitle) {
                noResultsHtml = `
                <div class="search-no-results">
                    <p>No results found for "${query}" in ${collectionTitle}</p>
                    <a href="/search?q=${encodeURIComponent(query)}&type=product" class="search-all-products">Search all products</a>
                </div>`;
            } else {
                noResultsHtml = `
                <div class="search-no-results">
                    <p>No results found for "${query}"</p>
                </div>`;
            }

            searchResults.innerHTML = noResultsHtml;
            searchResults.appendChild(searchLoader);
            searchResults.style.display = "block";
        }

        // Display error message
        function displayError() {
            searchLoader.classList.remove('active');
            searchResults.innerHTML = `
            <div class="search-error">
                <p>Error loading results. Please try again.</p>
            </div>`;
            searchResults.appendChild(searchLoader);
        }

        // Format price
        function formatPrice(price) {
            const numPrice = parseFloat(price);
            if (isNaN(numPrice)) return '';
            return '$' + numPrice.toFixed(2);
        }

        // Event Listeners
        
        // Handle search input
        searchInput.addEventListener("input", handleSearch);

        // Handle search button click - MODIFIED for custom template
        searchButton.addEventListener("click", function() {
            const query = searchInput.value.trim();
            if (query.length > 0) {
                let searchUrl;

                if (collectionHandle) {
                    // Direct to the same collection with the search filter template
                    searchUrl = `/collections/${collectionHandle}/search-filter?search=${encodeURIComponent(query)}`;
                } else {
                    searchUrl = `/search?q=${encodeURIComponent(query)}&type=product`;
                }

                window.location.href = searchUrl;
            }
        });

        // Handle Enter key press - MODIFIED for custom template
        searchInput.addEventListener("keydown", function(event) {
            if (event.key === "Enter") {
                const query = searchInput.value.trim();
                if (query.length > 0) {
                    let searchUrl;

                    if (collectionHandle) {
                        // Direct to the same collection with the search filter template
                        searchUrl = `/collections/${collectionHandle}/search-filter?search=${encodeURIComponent(query)}`;
                    } else {
                        searchUrl = `/search?q=${encodeURIComponent(query)}&type=product`;
                    }

                    window.location.href = searchUrl;
                }
                event.preventDefault();
            }
        });

        // Hide results when clicking outside
        document.addEventListener("click", function(event) {
            if (!searchInput.contains(event.target) && !searchResults.contains(event.target)) {
                searchResults.style.display = "none";
            }
        });
    });
});